import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import { useAuthStore } from '@/stores/authStore';
import { RouteMap } from '@/routes/Routes';
import { useAcceptInvite, usePublicInvite } from '@/features/betaOrganizations/hooks/useOrganizations';

/**
 * Public landing page for org invites. Renders a preview of the org and an Accept
 * button. Unauthenticated visitors are stashed into sessionStorage and bounced
 * through /login — the existing redirectAfterLogin flow brings them back here.
 */
const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? undefined;
  const navigate = useNavigate();
  const { userToken, userData, loading: authLoading } = useAuthStore();
  const isAuthenticated = !!userToken;

  const { data: invite, isLoading, error: previewError } = usePublicInvite(token);
  const acceptMutation = useAcceptInvite();
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const fullUrl = token ? `${RouteMap.ACCEPT_INVITE}?token=${encodeURIComponent(token)}` : RouteMap.ACCEPT_INVITE;

  // If the user isn't authenticated, stash the current URL so the login flow
  // brings them back after signup/login. Wait for auth init to finish so we
  // don't stash a stale redirect for users who are already logged in.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated && token) {
      sessionStorage.setItem('redirectAfterLogin', fullUrl);
    }
  }, [authLoading, isAuthenticated, token, fullUrl]);

  if (!token) {
    return (
      <CenteredCard>
        <Alert severity="error">This invitation link is missing a token.</Alert>
      </CenteredCard>
    );
  }

  if (isLoading) {
    return (
      <CenteredCard>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">Loading invitation…</Typography>
        </Stack>
      </CenteredCard>
    );
  }

  if (previewError || !invite) {
    return (
      <CenteredCard>
        <Alert severity="error">
          This invitation link is invalid or no longer exists. Ask the sender for a new invite.
        </Alert>
      </CenteredCard>
    );
  }

  if (invite.status === 'expired') {
    return (
      <CenteredCard>
        <Alert severity="warning">This invitation has expired. Ask the sender for a new invite.</Alert>
      </CenteredCard>
    );
  }
  if (invite.status === 'revoked') {
    return (
      <CenteredCard>
        <Alert severity="warning">This invitation was revoked by the team owner.</Alert>
      </CenteredCard>
    );
  }
  if (invite.status === 'accepted') {
    const orgPath = `${RouteMap.ORGANIZATIONS}/${invite.org_id}`;
    return (
      <CenteredCard>
        <Alert severity="info">This invitation has already been accepted.</Alert>
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          onClick={() => {
            if (!isAuthenticated) {
              sessionStorage.setItem('redirectAfterLogin', orgPath);
              navigate(RouteMap.LOGIN);
              return;
            }
            navigate(orgPath);
          }}
        >
          Go to {invite.org_name}
        </Button>
      </CenteredCard>
    );
  }

  const handleAccept = async () => {
    setAcceptError(null);
    try {
      const result = await acceptMutation.mutateAsync(token);
      sessionStorage.removeItem('redirectAfterLogin');
      if (result.warning) {
        sessionStorage.setItem(`orgJoinWarning:${result.org_id}`, result.warning);
      }
      navigate(`${RouteMap.ORGANIZATIONS}/${result.org_id}`);
    } catch (err: unknown) {
      // noteGoApiFetcher throws the parsed JSON body without status — rely on detail string.
      const errorObj = err as { detail?: unknown };
      const detail = typeof errorObj.detail === 'string' ? errorObj.detail : '';
      const detailLower = detail.toLowerCase();
      if (detailLower.includes('already a member of another organization')) {
        setAcceptError(detail);
      } else if (detailLower.includes('different email address')) {
        setAcceptError(detail);
      } else if (detailLower.includes('expired') || detailLower.includes('no longer pending')) {
        setAcceptError(detail || 'This invite is no longer valid.');
      } else if (detailLower.includes('seat limit')) {
        setAcceptError(detail);
      } else {
        setAcceptError('Failed to accept invitation. Please try again.');
      }
    }
  };

  return (
    <CenteredCard>
      <Stack alignItems="center" spacing={2}>
        <GroupAddOutlinedIcon sx={{ fontSize: 48, color: 'primary.main' }} />
        <Typography variant="h5" textAlign="center" fontWeight={600}>
          You&apos;re invited to {invite.org_name}
        </Typography>
        {invite.invited_by_email && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            <strong>{invite.invited_by_email}</strong> invited you to join the team on NoteGo.
          </Typography>
        )}

        {acceptError && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {acceptError}
          </Alert>
        )}

        {isAuthenticated ? (
          <>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Signed in as <strong>{userData?.email}</strong>. If this isn&apos;t the email that received the invite,
              log out and sign back in.
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={acceptMutation.isPending}
              startIcon={acceptMutation.isPending ? <CircularProgress size={18} /> : undefined}
              onClick={handleAccept}
            >
              {acceptMutation.isPending ? 'Joining…' : `Accept and join ${invite.org_name}`}
            </Button>
          </>
        ) : (
          <Stack spacing={1} sx={{ width: '100%' }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => {
                sessionStorage.setItem('redirectAfterLogin', fullUrl);
                navigate(RouteMap.LOGIN);
              }}
            >
              Sign up or log in to accept
            </Button>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              We&apos;ll bring you back here after you&apos;re signed in.
            </Typography>
          </Stack>
        )}
      </Stack>
    </CenteredCard>
  );
};

const CenteredCard = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      bgcolor: 'background.default',
    }}
  >
    <Paper elevation={2} sx={{ p: 4, maxWidth: 480, width: '100%' }}>
      {children}
    </Paper>
  </Box>
);

export default AcceptInvite;
